import { z } from "zod";

import { TRIP_EDIT_MIN_LEAD_HOURS } from "./leadTime.js";

// =============================================================================
// Role permissions
//
// A small, super-admin-editable matrix that controls which roles can
// perform a handful of operational actions. The toggles ENFORCE on both
// the server APIs and the UI — the source of truth is one settings
// table; the frontend reads each user's effective permissions off the
// session and the Permissions page reads/writes the whole matrix.
//
// Design rules:
//   * Super-admins always have every permission — never stored, never
//     editable (so you can't lock yourself out).
//   * Only the keys below are toggleable + enforced. Structural access
//     (submitting requests, managing users, editing this page, etc.) is
//     fixed in code and shown on the page for transparency only.
//   * Defaults exactly match the historical behavior, EXCEPT
//     `viewBookedTrips` is on for regular users (decided 2026-06-07 to
//     cut down ETA phone calls).
// =============================================================================

export const editablePermissionKeySchema = z.enum([
  "viewBookedTrips",
  "activateWillCall",
  "modifyTrip",
  "cancelTrip",
  "bookShortNotice",
  "editMember",
  "mergeDeleteMember",
  "deleteBooking",
  "viewMileageRate",
]);
export type EditablePermissionKey = z.infer<typeof editablePermissionKeySchema>;

export const EDITABLE_PERMISSION_KEYS: EditablePermissionKey[] = [
  "viewBookedTrips",
  "activateWillCall",
  "modifyTrip",
  "cancelTrip",
  "bookShortNotice",
  "editMember",
  "mergeDeleteMember",
  "deleteBooking",
  "viewMileageRate",
];

/** Per-role allow flags for one permission. Super-admin is always
 *  allowed and therefore not represented here. */
export const rolePermissionFlagsSchema = z.object({
  user: z.boolean(),
  admin: z.boolean(),
});
export type RolePermissionFlags = z.infer<typeof rolePermissionFlagsSchema>;

/** The full editable matrix: every editable key → its role flags. */
export const rolePermissionMatrixSchema = z.record(
  editablePermissionKeySchema,
  rolePermissionFlagsSchema,
);
export type RolePermissionMatrix = z.infer<typeof rolePermissionMatrixSchema>;

/** Code defaults. The settings table stores overrides; anything not
 *  overridden falls back to these. */
export const PERMISSION_DEFAULTS: Record<
  EditablePermissionKey,
  RolePermissionFlags
> = {
  viewBookedTrips: { user: true, admin: true },
  activateWillCall: { user: false, admin: true },
  modifyTrip: { user: false, admin: true },
  cancelTrip: { user: false, admin: true },
  bookShortNotice: { user: false, admin: true },
  editMember: { user: false, admin: true },
  mergeDeleteMember: { user: false, admin: true },
  deleteBooking: { user: false, admin: false },
  viewMileageRate: { user: false, admin: false },
};

/** Display metadata for the Permissions page — label + group per
 *  editable key, in render order. */
export interface PermissionMeta {
  key: EditablePermissionKey;
  label: string;
  description: string;
  group: string;
}

export const PERMISSION_META: PermissionMeta[] = [
  {
    key: "viewBookedTrips",
    label: "View Booked Trips",
    description: "See the dispatch board of booked trips (read-only by itself).",
    group: "Trips & dispatch",
  },
  {
    key: "activateWillCall",
    label: "Activate Will Call",
    description: "Flip a Will Call trip into the active dispatch queue.",
    group: "Trips & dispatch",
  },
  {
    key: "modifyTrip",
    label: "Modify a trip",
    description: `Change a trip's time or addresses (>${TRIP_EDIT_MIN_LEAD_HOURS}h out).`,
    group: "Trips & dispatch",
  },
  {
    key: "cancelTrip",
    label: "Cancel a trip",
    description: "Cancel a pending / assigned / Will Call trip.",
    group: "Trips & dispatch",
  },
  {
    key: "bookShortNotice",
    label: "Book at short notice",
    description:
      "Book inside the 30-minute minimum, with a reason. Everyone else is held to the lead time dispatch needs.",
    group: "Trips & dispatch",
  },
  {
    key: "editMember",
    label: "Edit member name / MRN / DOB",
    description: "Correct a patient's identity in the Members tab.",
    group: "Members",
  },
  {
    key: "mergeDeleteMember",
    label: "Merge or delete a member",
    description: "Combine duplicate profiles or remove a member.",
    group: "Members",
  },
  {
    key: "deleteBooking",
    label: "Delete a booking",
    description: "Remove an individual booking from the history.",
    group: "Members",
  },
  {
    key: "viewMileageRate",
    label: "View mileage & rate",
    description:
      "See each trip's mileage and its calculated price in the Bookings list and exports.",
    group: "Billing & rates",
  },
];

/** Fixed (non-editable) capabilities shown on the page for transparency.
 *  These aren't stored or toggleable — they're enforced structurally in
 *  code. `value` per role is one of: true (allowed), false (not), or a
 *  short label for nuanced cases. */
export interface FixedPermissionRow {
  label: string;
  description: string;
  group: string;
  user: boolean;
  admin: boolean | string;
  superAdmin: boolean | string;
}

export const FIXED_PERMISSION_ROWS: FixedPermissionRow[] = [
  {
    label: "Submit new trip requests",
    description: "The core booking form.",
    group: "Trips & dispatch",
    user: true,
    admin: true,
    superAdmin: true,
  },
  {
    label: "Track a trip's status",
    description: "Live ETA / Track My Trip.",
    group: "Trips & dispatch",
    user: true,
    admin: true,
    superAdmin: true,
  },
  {
    label: "Manage departments & locations",
    description: "The admin directory.",
    group: "Administration",
    user: false,
    admin: true,
    superAdmin: true,
  },
  {
    label: "View Reports & Audit log",
    description: "Operations dashboard + audit trail.",
    group: "Administration",
    user: false,
    admin: true,
    superAdmin: true,
  },
  {
    label: "Manage users & invitations",
    description: "Invite and manage accounts.",
    group: "Administration",
    user: false,
    admin: "Users only",
    superAdmin: "All accounts",
  },
  {
    label: "Edit this Permissions page",
    description: "Change who can do what.",
    group: "Administration",
    user: false,
    admin: false,
    superAdmin: true,
  },
];

// ---- API shapes -------------------------------------------------------------

export const permissionsResponseSchema = z.object({
  matrix: rolePermissionMatrixSchema,
});
export type PermissionsResponse = z.infer<typeof permissionsResponseSchema>;

export const permissionsUpdateRequestSchema = z.object({
  matrix: rolePermissionMatrixSchema,
});
export type PermissionsUpdateRequest = z.infer<
  typeof permissionsUpdateRequestSchema
>;

/** A resolved key→boolean map for one user's role, shipped on the
 *  session so the frontend can show/hide buttons and routes.
 *
 *  The KEY schema is a plain string, deliberately, even though the keys are
 *  really `EditablePermissionKey`. A browser keeps running the bundle it loaded
 *  until someone refreshes it, so after every deploy there is a window where an
 *  OLD bundle parses a NEW server's response. Validating keys against the enum
 *  meant that adding a permission made the session payload unparseable for
 *  every open tab: adding `bookShortNotice` in 1.21.39 produced "Unexpected
 *  response shape" (BAD_RESPONSE / contract_drift, on an HTTP 200) from a
 *  client still on 1.21.38, moments after it posted /api/auth/login.
 *
 *  Unknown keys are now carried through and simply ignored. Note this DOES
 *  widen the inferred type to `Record<string, boolean>`; what keeps call sites
 *  honest is `useHasPermission(key: EditablePermissionKey)`, which types the
 *  lookup, so a typo still fails to compile even though the map itself no
 *  longer constrains its keys. Values are still checked, because the UI shows
 *  and hides controls based on them. */
export const effectivePermissionsSchema = z.record(z.string(), z.boolean());
export type EffectivePermissions = z.infer<typeof effectivePermissionsSchema>;

/** Resolve a full matrix down to one role's effective permissions.
 *  Super-admins get everything. */
export function resolveEffectivePermissions(
  matrix: RolePermissionMatrix,
  role: "super_admin" | "admin" | "user",
): EffectivePermissions {
  const out: EffectivePermissions = {};
  for (const key of EDITABLE_PERMISSION_KEYS) {
    if (role === "super_admin") {
      out[key] = true;
    } else {
      const flags = matrix[key] ?? PERMISSION_DEFAULTS[key];
      out[key] = role === "admin" ? flags.admin : flags.user;
    }
  }
  return out;
}

/** Merge stored overrides over the code defaults into a complete
 *  matrix. */
export function mergeMatrixWithDefaults(
  overrides: Partial<RolePermissionMatrix>,
): RolePermissionMatrix {
  const out = {} as RolePermissionMatrix;
  for (const key of EDITABLE_PERMISSION_KEYS) {
    out[key] = overrides[key] ?? PERMISSION_DEFAULTS[key];
  }
  return out;
}
